function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { groupBy, first, map, flattenDeep } from 'lodash-es';
import { getUSPhoneNumberValidator, usPhoneNumberError } from 'validators';
import { convertStrToRichText } from 'seagull';
import { getProposalExpiryRange, isProposalExpiryValid } from "../../business-logic/expiryDateLogic";
import { validateAgreementRecipient, validateContact } from "../../business-logic/validators";
import { isUrlValid } from "../../components/AgreementStudioPage/AgreementStudioModal/IntroVideo/IntroVideoModal/IntroVIdeoModal.utils";
import { addCsvLineNumbers, createErrorObject, getEmptyStringError, isBoolField, isBoolTrue, parseCsv, validateCsvColumns, validateCsvRow, invalidEmailError, normalizeSpaces } from "../../components/ImportWizard/ImportWizard.utils";
import { daysOfWeek, defaultAgreementTitle, netTermsOptions } from "../../constants";
import { legalTermsModel, DraftStatusType } from "../../models";
import { makeAutoApprovalMethodNoticePeriod, makeManualApprovalMethodNoticePeriod } from "../../models/approval-method-notice-period";
import { NewContactModel } from "../../models/ContactModel";
import { makeExpirationDate } from "../../models/expiration-date";
import { makeIntroVideoLink } from "../../models/intro-video/makers";
import { RequestAccountantAccess } from "../../models/request-accountant-access";
import { isValidNewOngoingBilling } from "../../models/service/newOngoingBilling";
import { makeBillUpfrontState } from "../../models/service-billing";
import { extractBundles } from "../ImportServices/bundlesValidation.utils";
import { getServicesCsvColumnConfig, runBundleValidation } from "../ImportServices/parseAndValidateServicesCsv";
import { agreementBundleColumnsToValidate, isRowOfBundle } from "../ImportServices/servicesValidation.utils";
import { configureValidation, createServiceBundleFromCsvRow, createServiceFromCsvRow, dayOfMonthOptions, errorMessages, getAutoApprovalValidation, getDraftReviewers, getIsPaused, getIsRecurring, getSkipBillingOnAcceptance, parseEffectiveDate, parsePriceIncreaseStartsOn, validateAgreementsRecipients, validatePackages } from "./validateAgreements.utils";
const defaultCoverLetter = "Dear customer,\nWe've recently started using Anchor Automatic Billing as our billing service provider.\nThis allows us and you to save time and effort, as all invoices are generated automatically.\nSigning this digital agreement will allow your regular payments to happen automatically, and any changes to the agreement or to the payments will require your approval.\n";
const minAutoPriceIncreaseRate = 0.01;
const maxAutoPriceIncreaseRate = 100;
const getCsvColumnConfig = (PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD, UPLOAD_BUNDLES_BULK) => _objectSpread(_objectSpread({}, getServicesCsvColumnConfig({
  UPLOAD_BUNDLES_BULK
})), {}, {
  ['email']: _objectSpread(_objectSpread({
    inputName: 'email',
    required: true,
    manipulateValue: normalizeSpaces,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, invalidEmailError)
  }, configureValidation(validateContact.email)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['first name']: _objectSpread(_objectSpread({
    inputName: 'firstName',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, getEmptyStringError(headerName))
  }, configureValidation(validateContact.firstName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['last name']: _objectSpread(_objectSpread({
    inputName: 'lastName',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, getEmptyStringError(headerName))
  }, configureValidation(validateContact.lastName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['company name']: _objectSpread(_objectSpread({
    inputName: 'customerName',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, getEmptyStringError(headerName))
  }, configureValidation(validateContact.companyName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['agreement title']: {
    inputName: 'agreementTitle',
    shouldMatchSameAgreementServices: true
  },
  ['payment terms']: {
    inputName: 'paymentTerms',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.paymentTermsError),
    validate: paymentTerms => {
      const isUponReceipt = (paymentTerms === null || paymentTerms === void 0 ? void 0 : paymentTerms.toLowerCase()) === 'upon receipt';
      const containsAnExpectedNumber = netTermsOptions.includes(parseInt(paymentTerms));
      return isUponReceipt || containsAnExpectedNumber;
    },
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.paymentTermsError),
    shouldMatchSameAgreementServices: true
  },
  ['require payment method']: {
    inputName: 'requirePaymentMethod',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    validate: isBoolField,
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    shouldMatchSameAgreementServices: true
  },
  ['email body']: {
    inputName: 'emailBody',
    shouldMatchSameAgreementServices: true
  },
  ['phone number']: {
    inputName: 'buyerPhone',
    validate: buyerPhone => !buyerPhone || getUSPhoneNumberValidator()('+1' + buyerPhone) === true,
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, usPhoneNumberError),
    shouldMatchSameAgreementServices: true
  },
  ['billed upfront']: {
    inputName: 'billedUpfront',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    validate: isBoolField,
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    shouldMatchSameAgreementServices: false
  },
  ['billing day']: {
    inputName: 'billingDay',
    required: false,
    validate: (billingDay, _ref) => {
      let {
        rowObject
      } = _ref;
      const billingOccurrence = (isRowOfBundle(rowObject) ? rowObject.bundleBillingOccurrence : rowObject.billingOccurrence).toLowerCase();
      const isRecurringBilling = ['monthly', 'weekly', 'biweekly'].includes(billingOccurrence);
      const isValidOneTimeBilling = !isRecurringBilling && billingDay === '';
      const isLast = billingDay.toLowerCase() === 'last';
      const containsAnExpectedNumber = dayOfMonthOptions().includes(parseInt(billingDay));
      const isDayOfWeek = daysOfWeek.map(day => day.toLowerCase()).includes(billingDay.toLowerCase());
      const isValidMonthly = billingOccurrence === 'monthly' && (isLast || containsAnExpectedNumber);
      const isValidWeekly = ['weekly', 'biweekly'].includes(billingOccurrence) && isDayOfWeek;
      return {
        valid: isValidMonthly || isValidWeekly || isValidOneTimeBilling,
        errorType: rowObject.bundleBillingOccurrence ? 'bundles' : 'services'
      };
    },
    validateError: (headerName, rowNumber, errorType) => createErrorObject(headerName, rowNumber, errorMessages.billingDayError(errorType)),
    shouldMatchSameAgreementServices: false
  },
  ['effective date']: {
    inputName: 'effectiveDate',
    validate: (effectiveDate, _ref2) => {
      let {
        now
      } = _ref2;
      return parseEffectiveDate(effectiveDate, now).toValidateResult();
    },
    validateError: (headerName, rowNumber, errorType) => createErrorObject(headerName, rowNumber, errorType || ''),
    shouldMatchSameAgreementServices: true
  },
  ['accountant access']: {
    inputName: 'accountantAccess',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    validate: accountantAccess => isBoolField(accountantAccess),
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    shouldMatchSameAgreementServices: true
  },
  ['mark this service as optional']: {
    inputName: 'optionalService',
    required: false,
    validate: optionalService => {
      return !optionalService || ['included', 'not included'].includes(optionalService.toLowerCase());
    },
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.optionalServiceError),
    shouldMatchSameAgreementServices: false
  },
  ['terms and conditions']: {
    inputName: 'termsAndConditions',
    required: false,
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.termsAndConditionsError),
    shouldMatchSameAgreementServices: true
  },
  ['issue invoice on acceptance']: {
    inputName: 'issueInvoiceOnAcceptance',
    validate: issueInvoiceOnAcceptance => isBoolField(issueInvoiceOnAcceptance),
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName))
  },
  ['pause automatic billing']: {
    inputName: 'startPaused',
    validate: startPaused => isBoolField(startPaused),
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName))
  },
  ['agreement note']: {
    inputName: 'extraClause',
    shouldMatchSameAgreementServices: true
  },
  ['amendments auto approval']: getAutoApprovalValidation('amendmentsAutoApproval'),
  ['packages']: {
    inputName: 'packages',
    required: false
  },
  ['intro video']: {
    inputName: 'introVideoUrl',
    required: false,
    shouldMatchSameAgreementServices: true,
    validate: introVideoUrl => {
      if (introVideoUrl === '') {
        return true;
      }
      return isUrlValid(introVideoUrl);
    },
    validateError: (headerName, rowNumber) => {
      return createErrorObject(headerName, rowNumber, errorMessages.introVideoUrlError);
    }
  },
  ['automatic yearly price increase rate']: {
    inputName: 'priceIncreaseRate',
    validate(priceIncreaseRate) {
      if (!priceIncreaseRate) {
        return true;
      }
      const asNumber = +priceIncreaseRate;
      return !isNaN(asNumber) && asNumber >= minAutoPriceIncreaseRate && asNumber <= maxAutoPriceIncreaseRate;
    },
    validateError(headerName, rowNumber) {
      return createErrorObject(headerName, rowNumber, "Invalid price increase rate. Please enter a number between ".concat(minAutoPriceIncreaseRate, " and ").concat(maxAutoPriceIncreaseRate));
    }
  },
  ['automatic yearly price increase starts on']: {
    inputName: 'priceIncreaseStartsOn',
    validate: (priceIncreaseStartsOn, _ref3) => {
      let {
        now = new Date(),
        rowObject: {
          effectiveDate
        }
      } = _ref3;
      return parsePriceIncreaseStartsOn(priceIncreaseStartsOn, effectiveDate, now).toValidateResult();
    },
    validateError: (headerName, rowNumber, errorType) => createErrorObject(headerName, rowNumber, errorType || '')
  },
  ['out of scope charges auto approval']: getAutoApprovalValidation('invoicesAutoApproval'),
  ['recipient 1 first name']: _objectSpread(_objectSpread({
    inputName: 'recipient1FirstName',
    required: false
  }, configureValidation(validateAgreementRecipient.firstName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 1 last name']: _objectSpread(_objectSpread({
    inputName: 'recipient1LastName',
    required: false
  }, configureValidation(validateAgreementRecipient.lastName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 1 email']: _objectSpread(_objectSpread({
    inputName: 'recipient1Email',
    required: false,
    manipulateValue: normalizeSpaces
  }, configureValidation(validateAgreementRecipient.email)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 1 required to sign']: {
    inputName: 'recipient1RequiredToSign',
    required: false,
    validate: requiredToSign => !requiredToSign || isBoolField(requiredToSign),
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    shouldMatchSameAgreementServices: true
  },
  ['recipient 2 first name']: _objectSpread(_objectSpread({
    inputName: 'recipient2FirstName',
    required: false
  }, configureValidation(validateAgreementRecipient.firstName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 2 last name']: _objectSpread(_objectSpread({
    inputName: 'recipient2LastName',
    required: false
  }, configureValidation(validateAgreementRecipient.lastName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 2 email']: _objectSpread(_objectSpread({
    inputName: 'recipient2Email',
    required: false,
    manipulateValue: normalizeSpaces
  }, configureValidation(validateAgreementRecipient.email)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 2 required to sign']: {
    inputName: 'recipient2RequiredToSign',
    required: false,
    validate: requiredToSign => !requiredToSign || isBoolField(requiredToSign),
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    shouldMatchSameAgreementServices: true
  },
  ['recipient 3 first name']: _objectSpread(_objectSpread({
    inputName: 'recipient3FirstName',
    required: false
  }, configureValidation(validateAgreementRecipient.firstName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 3 last name']: _objectSpread(_objectSpread({
    inputName: 'recipient3LastName',
    required: false
  }, configureValidation(validateAgreementRecipient.lastName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 3 email']: _objectSpread(_objectSpread({
    inputName: 'recipient3Email',
    required: false,
    manipulateValue: normalizeSpaces
  }, configureValidation(validateAgreementRecipient.email)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 3 required to sign']: {
    inputName: 'recipient3RequiredToSign',
    required: false,
    validate: requiredToSign => !requiredToSign || isBoolField(requiredToSign),
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    shouldMatchSameAgreementServices: true
  },
  ['recipient 4 first name']: _objectSpread(_objectSpread({
    inputName: 'recipient4FirstName',
    required: false
  }, configureValidation(validateAgreementRecipient.firstName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 4 last name']: _objectSpread(_objectSpread({
    inputName: 'recipient4LastName',
    required: false
  }, configureValidation(validateAgreementRecipient.lastName)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 4 email']: _objectSpread(_objectSpread({
    inputName: 'recipient4Email',
    required: false,
    manipulateValue: normalizeSpaces
  }, configureValidation(validateAgreementRecipient.email)), {}, {
    shouldMatchSameAgreementServices: true
  }),
  ['recipient 4 required to sign']: {
    inputName: 'recipient4RequiredToSign',
    required: false,
    validate: requiredToSign => !requiredToSign || isBoolField(requiredToSign),
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    shouldMatchSameAgreementServices: true
  }
}, PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD ? {
  ['expiration period in days']: {
    inputName: 'proposalExpiryDate',
    required: false,
    shouldMatchSameAgreementServices: true,
    validate(value) {
      const days = +value;
      const isValidProposalExpiryPeriod = !isNaN(days) && Number.isInteger(days) && isProposalExpiryValid(days);
      return value === '' || isValidProposalExpiryPeriod;
    },
    validateError: (headerName, rowNumber) => {
      const autoProposalExpiryRange = getProposalExpiryRange();
      return createErrorObject(headerName, rowNumber, "Invalid proposal expiration period. Please select a number between ".concat(autoProposalExpiryRange.minDays, " and ").concat(autoProposalExpiryRange.maxDays));
    }
  }
} : {});
function runValidation(_ref4) {
  let {
    parsedCsv,
    availableLegalTerms,
    now,
    csvColumnConfig,
    UPLOAD_BUNDLES_BULK
  } = _ref4;
  const errorContext = {
    phase: 'validation'
  };
  if (!parsedCsv.length) throw new Error('No data');
  validateCsvColumns(parsedCsv, csvColumnConfig);
  const withLineNumbers = addCsvLineNumbers(parsedCsv);
  const groupedServicesByAgreement = groupBy(withLineNumbers, _ref5 => {
    let {
      email,
      customerName
    } = _ref5;
    return "".concat(email, ":").concat(customerName);
  });
  const errors = map(groupedServicesByAgreement, serviceRows => {
    const packagesError = validatePackages(serviceRows);
    if (packagesError) {
      return packagesError;
    }
    if (UPLOAD_BUNDLES_BULK) {
      const groupedServicesByAgreementPackage = groupBy(serviceRows, _ref6 => {
        let {
          packages
        } = _ref6;
        return packages;
      });
      for (const packageName of Object.keys(groupedServicesByAgreementPackage)) {
        const {
          bundleRows: bundles
        } = extractBundles(groupedServicesByAgreementPackage[packageName]);
        const bundleErrors = runBundleValidation(bundles, agreementBundleColumnsToValidate);
        if (bundleErrors.length > 0) {
          return bundleErrors;
        }
      }
    }
    return serviceRows.map(rowObject => {
      errorContext['service'] = rowObject.serviceName;
      errorContext['lineNumber'] = rowObject.lineNumber;
      try {
        const recipientsError = validateAgreementsRecipients(rowObject);
        if (recipientsError) {
          return recipientsError;
        }
        return validateCsvRow(rowObject, csvColumnConfig, (columnConfig, columnName) => {
          const {
            inputName,
            shouldMatchSameAgreementServices
          } = columnConfig;
          const {
            lineNumber,
            billedUpfront,
            automaticBilling,
            issueInvoiceOnAcceptance,
            effectiveDate,
            priceIncreaseRate
          } = rowObject;
          const value = rowObject[inputName];
          const isRecurring = getIsRecurring(rowObject);
          const isAutomaticBilling = isBoolTrue(automaticBilling);
          const isOngoing = isRecurring && isAutomaticBilling;
          const isBilledUpfront = isBoolTrue(billedUpfront);
          const isPaused = getIsPaused(rowObject);
          const isValidSkipBillingOnAcceptance = isBoolField(issueInvoiceOnAcceptance || '');
          const skipBillingOnAcceptance = getSkipBillingOnAcceptance(rowObject);
          if (shouldMatchSameAgreementServices && value !== serviceRows[0][inputName]) {
            return createErrorObject(columnName, lineNumber, errorMessages.sameAgreementUnmatchingDataError);
          }
          if (inputName === 'termsAndConditions' && typeof value === 'string' && value !== '' && !availableLegalTerms.some(doc => legalTermsModel.name(doc).toLowerCase() === value.toLowerCase() || legalTermsModel.name(doc).toLowerCase() === value.toLowerCase() + '.pdf')) {
            return createErrorObject(columnName, lineNumber, errorMessages.termsAndConditionsError);
          }
          if (inputName === 'billingDay' && isBilledUpfront && typeof value === 'string' && value.toLowerCase() === 'last') {
            return createErrorObject(columnName, lineNumber, errorMessages.billUpfrontLastDayError);
          }
          if (inputName === 'issueInvoiceOnAcceptance' && isValidSkipBillingOnAcceptance && isOngoing && !isValidNewOngoingBilling({
            isBilledUpfront,
            isPaused,
            isProrated: false,
            billUpfrontState: makeBillUpfrontState(skipBillingOnAcceptance),
            startBillingAt: false
          })) {
            return createErrorObject(columnName, lineNumber, 'You can issue an invoice on acceptance only for automatic, recurring, unpaused, billed-upfront services');
          }
          if ((inputName === 'priceIncreaseRate' || inputName === 'priceIncreaseStartsOn') && !!value && !isRecurring) {
            const serviceType = isRowOfBundle(rowObject) ? 'bundles' : 'services';
            return createErrorObject(columnName, lineNumber, errorMessages.priceIncreaseOnlyForRecurringError(serviceType));
          }
          if (inputName === 'priceIncreaseStartsOn') {
            if (!value && priceIncreaseRate && effectiveDate) {
              return createErrorObject(columnName, lineNumber, 'Automatic yearly price increase start date is required if effective date is set');
            }
            if (value && !priceIncreaseRate) {
              return createErrorObject(columnName, lineNumber, 'Automatic yearly price increase rate is required if start date is set');
            }
          }
        }, now);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (exn) {
        // eslint-disable-next-line no-console
        console.error({
          error: '' + exn,
          errorContext
        });
        if ((exn === null || exn === void 0 ? void 0 : exn.message) != null) {
          errorContext['exn'] = exn.message;
          exn.message = JSON.stringify(errorContext);
        }
        throw exn;
      }
    });
  });
  return flattenDeep(errors);
}
const createDraft = (rows, context, PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD) => {
  const {
    now,
    availableLegalTerms,
    defaultDraftsValues,
    generateUniqueId,
    UPLOAD_BUNDLES_BULK
  } = context;
  const errorContext = {};
  try {
    const withLineNumbers = addCsvLineNumbers(rows);
    const firstRow = first(withLineNumbers);
    if (firstRow == null) throw new Error('empty CSV');
    const {
      email,
      firstName,
      lastName,
      buyerPhone,
      customerName,
      agreementTitle,
      paymentTerms,
      requirePaymentMethod,
      emailBody,
      effectiveDate,
      accountantAccess,
      termsAndConditions,
      amendmentsAutoApproval,
      proposalExpiryDate,
      extraClause,
      invoicesAutoApproval
    } = firstRow;
    errorContext['email'] = email;
    errorContext['customerName'] = customerName;
    errorContext['agreementTitle'] = agreementTitle;
    const isUponReceipt = (paymentTerms === null || paymentTerms === void 0 ? void 0 : paymentTerms.toLowerCase()) === 'upon receipt';
    const netTerms = isUponReceipt ? 0 : parseInt(paymentTerms);
    const allIncludedServicesToCreateAsServices = [];
    const packages = groupBy(withLineNumbers, _ref7 => {
      let {
        packages
      } = _ref7;
      return packages;
    });
    const packageNames = Object.keys(packages);
    const serviceBundles = packageNames.reduce((acc, packageName) => {
      const packageRows = packages[packageName];
      const {
        bundleRows,
        nonBundleRows
      } = extractBundles(packageRows);
      if (UPLOAD_BUNDLES_BULK) {
        const bundles = bundleRows.reduce((acc, b) => {
          if (!acc[b.bundleName]) {
            acc[b.bundleName] = [];
          }
          acc[b.bundleName].push(b);
          return acc;
        }, {});
        const bundleServices = [];
        Object.entries(bundles).forEach(_ref8 => {
          let [bundleName, rows] = _ref8;
          errorContext['phase'] = 'create-service-bundle';
          errorContext['included-service'] = bundleName;
          const {
            bundleService,
            includedServicesToCreateAsServices
          } = createServiceBundleFromCsvRow(rows, context);
          bundleServices.push(bundleService);
          allIncludedServicesToCreateAsServices.push(...includedServicesToCreateAsServices);
        });
        const services = nonBundleRows.map(r => {
          errorContext['phase'] = 'create-service';
          errorContext['service'] = r.serviceName;
          return createServiceFromCsvRow(r, context);
        });
        acc.bundles.push({
          id: generateUniqueId('bundle'),
          name: packageName,
          services: [...bundleServices, ...services]
        });
      } else {
        const services = packageRows.map(r => {
          errorContext['phase'] = 'create-service';
          errorContext['service'] = r.serviceName;
          return createServiceFromCsvRow(r, context);
        });
        acc.bundles.push({
          id: generateUniqueId('bundle'),
          name: packageName,
          services
        });
      }
      return acc;
    }, {
      bundles: []
    });
    errorContext['phase'] = undefined;
    errorContext['service'] = undefined;
    const parsedEffectiveDate = parseEffectiveDate(effectiveDate, now);
    const legalTermsDoc = availableLegalTerms.find(doc => legalTermsModel.name(doc).toLowerCase() === termsAndConditions.toLowerCase() || legalTermsModel.name(doc).toLowerCase() === termsAndConditions.toLowerCase() + '.pdf');
    return {
      draft: {
        id: generateUniqueId('draft'),
        coverLetter: defaultCoverLetter,
        extraClause: convertStrToRichText(extraClause || ''),
        requirePaymentMethod: isBoolTrue(requirePaymentMethod),
        allowManualPayments: false,
        isExistingCustomer: false,
        customNotificationText: (emailBody === null || emailBody === void 0 ? void 0 : emailBody.length) > 0 ? emailBody : '',
        title: agreementTitle.length > 0 ? agreementTitle : defaultAgreementTitle,
        lastModifiedOn: Date.now(),
        netTerms,
        effectiveDate: parsedEffectiveDate.datestamp(),
        legalTerms: legalTermsDoc && legalTermsModel.toDraftLegalTermsOld(legalTermsDoc, false) || defaultDraftsValues.legalTerms || null,
        legalTermsDocuments: legalTermsDoc ? [legalTermsModel.toRelationshipLegalTermsDocuments(legalTermsDoc)] : [],
        feeControls: {
          coverCreditCardFees: false,
          coverDirectDebitFees: false
        },
        clientContactId: null,
        isSample: false,
        amendmentsApprovalMethod: amendmentsAutoApproval ? makeAutoApprovalMethodNoticePeriod(parseInt(amendmentsAutoApproval)) : makeManualApprovalMethodNoticePeriod(),
        invoicesApprovalMethod: invoicesAutoApproval ? makeAutoApprovalMethodNoticePeriod(parseInt(invoicesAutoApproval)) : makeManualApprovalMethodNoticePeriod(),
        requestAccountantAccess: isBoolTrue(accountantAccess) ? RequestAccountantAccess.Mandatory : RequestAccountantAccess.None,
        serviceBundles: packageNames.length > 1 ? serviceBundles : undefined,
        services: packageNames.length > 1 ? [] : serviceBundles.bundles[0].services,
        reviewers: getDraftReviewers(firstRow),
        introVideo: rows[0].introVideoUrl ? makeIntroVideoLink(rows[0].introVideoUrl) : null,
        expirationDate: makeExpirationDate(PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD ? Number(proposalExpiryDate) : undefined),
        status: {
          type: DraftStatusType.Active
        }
      },
      contact: NewContactModel.make({
        email: email,
        firstName: firstName,
        lastName: lastName,
        phone: buyerPhone ? '+1' + buyerPhone : null,
        companyName: customerName
      }),
      includedServicesToCreateIfNotExists: allIncludedServicesToCreateAsServices
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (exn) {
    // eslint-disable-next-line no-console
    console.error({
      error: '' + exn,
      errorContext
    });
    if ((exn === null || exn === void 0 ? void 0 : exn.message) != null) {
      errorContext['exn'] = exn.message;
      exn.message = JSON.stringify(errorContext);
    }
    throw exn;
  }
};
const createDrafts = (context, PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD) => {
  const {
    parsedCsv
  } = context;
  const groupedServicesByAgreement = groupBy(parsedCsv, _ref9 => {
    let {
      email,
      customerName
    } = _ref9;
    return "".concat(email, ":").concat(customerName);
  });
  return map(groupedServicesByAgreement, serviceRows => createDraft(serviceRows, context, PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD));
};
function tryToParseError(error, or) {
  try {
    return JSON.parse(error);
  } catch (e) {
    return or;
  }
}
export const parseAndValidateAgreementCsv = async _ref10 => {
  let {
    data,
    defaultDraftsValues,
    availableLegalTerms,
    now,
    PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD,
    generateUniqueId,
    UPLOAD_BUNDLES_BULK
  } = _ref10;
  try {
    const csvColumnConfig = getCsvColumnConfig(PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD, UPLOAD_BUNDLES_BULK);
    const parsedCsv = await parseCsv(data, csvColumnConfig);
    const context = {
      defaultDraftsValues,
      availableLegalTerms,
      now,
      parsedCsv,
      csvColumnConfig,
      generateUniqueId,
      UPLOAD_BUNDLES_BULK
    };
    const errors = runValidation(context);
    if (!errors.length) {
      const items = createDrafts(context, PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD);
      return {
        items,
        errors: []
      };
    } else {
      return {
        items: [],
        errors
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (exn) {
    var _tryToParseError;
    return {
      items: [],
      errors: [{
        message: '' + exn,
        rowNumber: ((_tryToParseError = tryToParseError(exn === null || exn === void 0 ? void 0 : exn.message, 0)) === null || _tryToParseError === void 0 ? void 0 : _tryToParseError.lineNumber) || 0
      }]
    };
  }
};